import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import blogArticles from '../data/blogArticles';

const PLACEHOLDER = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="800" height="450" fill="%231B3A5C"><rect width="800" height="450"/><text x="400" y="225" text-anchor="middle" fill="%23ffffff44" font-size="24" font-family="sans-serif">NOMOKRATIA.AI</text></svg>');

function BlogCard({ article, featured = false }) {
  const img = article.image || PLACEHOLDER;

  if (featured) {
    return (
      <Link to={`/blog/${article.slug}`} className="block group mb-10">
        <div className="relative rounded-2xl overflow-hidden" style={{ minHeight: 420 }}>
          <img src={img} alt={article.title} className="w-full h-full object-cover absolute inset-0" style={{ minHeight: 420 }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <div className="flex items-center gap-3 mb-3">
              <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">{article.category}</span>
              <span className="text-white/70 text-sm">{article.readTime}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 group-hover:text-blue-300 transition-colors leading-tight">{article.title}</h2>
            <p className="text-white/80 text-base max-w-2xl">{article.excerpt}</p>
            <div className="mt-4 text-blue-400 font-semibold text-sm group-hover:text-blue-300 transition-colors">
              {"\u0386\u03c1\u03b8\u03c1\u03bf \u2192"}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/blog/${article.slug}`} className="block group">
      <div className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-xl hover:border-blue-300 transition-all h-full">
        <div className="aspect-video overflow-hidden">
          <img src={img} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        </div>
        <div className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">{article.category}</span>
            <span className="text-gray-400 text-xs">{article.readTime}</span>
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors leading-tight">{article.title}</h3>
          <p className="text-gray-500 text-sm leading-relaxed">{article.excerpt}</p>
        </div>
      </div>
    </Link>
  );
}

export default function Blog() {
  const featured = blogArticles[0];
  const rest = blogArticles.slice(1);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "NOMOKRATIA.AI Blog",
    "description": "\u0386\u03c1\u03b8\u03c1\u03b1 \u03b3\u03b9\u03b1 \u03c4\u03b5\u03c7\u03bd\u03b7\u03c4\u03ae \u03bd\u03bf\u03b7\u03bc\u03bf\u03c3\u03cd\u03bd\u03b7, \u03b4\u03af\u03ba\u03b1\u03b9\u03bf \u03ba\u03b1\u03b9 \u03c4\u03b5\u03c7\u03bd\u03bf\u03bb\u03bf\u03b3\u03af\u03b1",
    "url": "https://nomokratia.ai/blog",
    "publisher": {
      "@type": "Organization",
      "name": "PROTOS AI Agency IKE",
      "url": "https://ai-agency.gr"
    },
    "inLanguage": "el",
    "blogPost": blogArticles.map(a => ({
      "@type": "BlogPosting",
      "headline": a.title,
      "datePublished": a.date,
      "url": `https://nomokratia.ai/blog/${a.slug}`
    }))
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Blog | NOMOKRATIA.AI — \u03a4\u03b5\u03c7\u03bd\u03b7\u03c4\u03ae \u039d\u03bf\u03b7\u03bc\u03bf\u03c3\u03cd\u03bd\u03b7 & \u0394\u03af\u03ba\u03b1\u03b9\u03bf</title>
        <meta name="description" content="\u0386\u03c1\u03b8\u03c1\u03b1 \u03b3\u03b9\u03b1 AI \u03c3\u03c4\u03bf\u03bd \u03bd\u03bf\u03bc\u03b9\u03ba\u03cc \u03c4\u03bf\u03bc\u03ad\u03b1, \u03bd\u03bf\u03bc\u03bf\u03bb\u03bf\u03b3\u03af\u03b1, \u03c3\u03cd\u03bd\u03c4\u03b1\u03be\u03b7 \u03b5\u03b3\u03b3\u03c1\u03ac\u03c6\u03c9\u03bd, EU AI Act \u03ba\u03b1\u03b9 LegalTech \u03c3\u03c4\u03b7\u03bd \u0395\u03bb\u03bb\u03ac\u03b4\u03b1." />
        <link rel="canonical" href="https://nomokratia.ai/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Blog | NOMOKRATIA.AI" />
        <meta property="og:description" content="\u0386\u03c1\u03b8\u03c1\u03b1 \u03b3\u03b9\u03b1 \u03c4\u03b5\u03c7\u03bd\u03b7\u03c4\u03ae \u03bd\u03bf\u03b7\u03bc\u03bf\u03c3\u03cd\u03bd\u03b7, \u03b4\u03af\u03ba\u03b1\u03b9\u03bf \u03ba\u03b1\u03b9 \u03c4\u03b5\u03c7\u03bd\u03bf\u03bb\u03bf\u03b3\u03af\u03b1" />
        <meta property="og:url" content="https://nomokratia.ai/blog" />
        <meta property="og:locale" content="el_GR" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://customer-assets.emergentagent.com/job_ailegalpro/artifacts/wofbxh36_Untitled%20design%20%2821%29.png" alt="NOMOKRATIA Logo" className="h-12 w-auto" />
          </a>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="/" className="text-gray-600 hover:text-slate-900 text-sm font-medium">{"\u0391\u03c1\u03c7\u03b9\u03ba\u03ae"}</a>
            <a href="/#features" className="text-gray-600 hover:text-slate-900 text-sm font-medium">{"\u0394\u03c5\u03bd\u03b1\u03c4\u03cc\u03c4\u03b7\u03c4\u03b5\u03c2"}</a>
            <a href="/#database" className="text-gray-600 hover:text-slate-900 text-sm font-medium">{"\u0392\u03ac\u03c3\u03b7 \u0394\u03b5\u03b4\u03bf\u03bc\u03ad\u03bd\u03c9\u03bd"}</a>
            <span className="text-slate-900 text-sm font-bold border-b-2 border-slate-900 pb-1">Blog</span>
          </nav>
        </div>
      </header>

      <main className="container mx-auto max-w-6xl py-10 px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Blog</h1>
          <p className="text-gray-500 text-lg">{"\u0386\u03c1\u03b8\u03c1\u03b1 \u03b3\u03b9\u03b1 \u03c4\u03b5\u03c7\u03bd\u03b7\u03c4\u03ae \u03bd\u03bf\u03b7\u03bc\u03bf\u03c3\u03cd\u03bd\u03b7, \u03b4\u03af\u03ba\u03b1\u03b9\u03bf \u03ba\u03b1\u03b9 \u03c4\u03b5\u03c7\u03bd\u03bf\u03bb\u03bf\u03b3\u03af\u03b1"}</p>
        </div>

        {featured && <BlogCard article={featured} featured />}

        <div className="grid md:grid-cols-3 gap-6">
          {rest.map((article) => (
            <BlogCard key={article.slug} article={article} />
          ))}
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 py-8 px-4 text-center mt-12">
        <p className="text-gray-500 text-sm">&copy; 2026 NOMOKRATIA.AI by PROTOS AI Agency IKE</p>
      </footer>
    </div>
  );
}
